/**
 * The single integration point with SurveyJS.
 *
 * The definition always comes from the backend — there is no survey JSON anywhere in this
 * codebase. The runner restores saved state (which is what makes pause/resume exact), autosaves
 * on value and page change, routes file questions to the uploads API, and posts the final
 * submission.
 *
 * It also feeds the survey two things the library cannot know on its own:
 *
 *   `deep_dive`            — whether the operator asked for full discovery or the essentials
 *   `uploaded_*` flags     — which files have already arrived, so questions a file answers can
 *                            stand down instead of asking someone to re-type them
 *
 * and reports section-level progress upward so the shell can show where you are, why you are
 * being asked, and what is left.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Model, Serializer, type PageModel, type Question, type SurveyModel } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";

import { uploads as uploadsApi } from "../api/resources";
import {
  computeProgress,
  uploadFlags,
  type PageSnapshot,
  type SurveyProgress,
  type UploadLike,
} from "../lib/surveyProgress";

// The compiler emits these on every element/page so the client can address answers by their
// stable identifier and group them by business area.
const CUSTOM_PROPERTIES: Array<[string, string]> = [
  ["question", "semanticId"],
  ["question", "uploadCategory"],
  ["question", "whyAsked"],
  ["question", "uploadInstead"],
  ["page", "moduleKey"],
  ["page", "section"],
  ["page", "sectionLabel"],
  ["page", "sectionWhy"],
  ["page", "depth"],
];

for (const [owner, name] of CUSTOM_PROPERTIES) {
  if (!Serializer.findProperty(owner, name)) {
    Serializer.addProperty(owner, { name, category: "general" });
  }
}

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface SurveyControl {
  goToPage: (index: number) => void;
  goToSection: (key: string) => void;
  next: () => void;
  previous: () => void;
  complete: () => void;
}

export interface SurveyRunnerProps {
  definition: Record<string, unknown>;
  initialData?: Record<string, unknown>;
  initialPageNo?: number;
  readOnly?: boolean;
  organizationId: string;
  sessionId?: string;
  projectId?: string;
  /** Full discovery vs the essentials. Drives the `deep_dive` variable the library branches on. */
  deepDive?: boolean;
  uploads?: UploadLike[];
  onSave: (payload: {
    data: Record<string, unknown>;
    currentPageNo: number;
    progressPercent: number;
  }) => Promise<void>;
  onComplete: (payload: { data: Record<string, unknown>; currentPageNo: number }) => Promise<void>;
  onStatusChange?: (status: SaveStatus, message?: string) => void;
  onProgress?: (progress: SurveyProgress) => void;
  controlRef?: { current: SurveyControl | null };
}

const AUTOSAVE_DEBOUNCE_MS = 900;

function snapshot(model: SurveyModel): PageSnapshot[] {
  const visibleOrder = new Map<string, number>();
  model.visiblePages.forEach((page: PageModel, index: number) => visibleOrder.set(page.name, index));

  return model.pages.map((page: PageModel) => ({
    name: page.name,
    title: page.title || page.name,
    section: String(page.getPropertyValue("section") ?? "operations"),
    sectionLabel: String(page.getPropertyValue("sectionLabel") ?? page.title ?? page.name),
    sectionWhy: String(page.getPropertyValue("sectionWhy") ?? ""),
    depth: page.getPropertyValue("depth") === "detail" ? "detail" : "essential",
    visible: page.isVisible,
    navIndex: visibleOrder.get(page.name) ?? -1,
    questions: page.questions.map((question: Question) => ({
      name: question.name,
      semanticId: question.getPropertyValue("semanticId") as string | undefined,
      visible: question.isVisible,
      required: question.isRequired,
      answered: !question.isEmpty(),
    })),
  }));
}

export function SurveyRunner({
  definition,
  initialData,
  initialPageNo,
  readOnly,
  organizationId,
  sessionId,
  projectId,
  deepDive = false,
  uploads,
  onSave,
  onComplete,
  onStatusChange,
  onProgress,
  controlRef,
}: SurveyRunnerProps) {
  const [error, setError] = useState<string | undefined>();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef(false);

  const survey = useMemo(() => {
    const model = new Model(definition);
    model.showCompletedPage = false;
    model.showProgressBar = "off"; // the shell shows progress by answers, not pages
    if (readOnly) model.mode = "display";
    if (initialData) model.data = initialData;
    if (initialPageNo && initialPageNo < model.visiblePageCount) {
      model.currentPageNo = initialPageNo;
    }
    return model;
    // A new model per definition/session; state is applied above rather than by re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition, sessionId, readOnly]);

  const report = useCallback(
    (model: SurveyModel) => {
      if (!onProgress) return;
      onProgress(computeProgress(snapshot(model), model.currentPageNo));
    },
    [onProgress],
  );

  // Variables the library branches on. Set before anything renders so no question flashes in
  // and then disappears once its file is noticed.
  useEffect(() => {
    survey.setVariable("deep_dive", Boolean(deepDive));
    const flags = uploadFlags(uploads ?? []);
    for (const [name, value] of Object.entries(flags)) survey.setVariable(name, value);
    report(survey);
  }, [survey, deepDive, uploads, report]);

  useEffect(() => {
    if (!controlRef) return undefined;
    controlRef.current = {
      goToPage: (index: number) => {
        if (index >= 0 && index < survey.visiblePageCount) survey.currentPageNo = index;
      },
      goToSection: (key: string) => {
        const target = survey.visiblePages.findIndex(
          (page: PageModel) => page.getPropertyValue("section") === key,
        );
        if (target >= 0) survey.currentPageNo = target;
      },
      next: () => survey.nextPage(),
      previous: () => survey.prevPage(),
      complete: () => survey.tryComplete(),
    };
    return () => {
      controlRef.current = null;
    };
  }, [survey, controlRef]);

  useEffect(() => {
    if (readOnly) {
      report(survey);
      return undefined;
    }

    const progressOf = (model: SurveyModel) => {
      const computed = computeProgress(snapshot(model), model.currentPageNo);
      return computed.percent;
    };

    const flush = async (model: SurveyModel) => {
      if (pending.current) return;
      pending.current = true;
      onStatusChange?.("saving");
      try {
        await onSave({
          data: model.data as Record<string, unknown>,
          currentPageNo: model.currentPageNo,
          progressPercent: progressOf(model),
        });
        setError(undefined);
        onStatusChange?.("saved");
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : String(cause);
        setError(message);
        onStatusChange?.("error", message);
      } finally {
        pending.current = false;
      }
    };

    const schedule = (model: SurveyModel) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(model), AUTOSAVE_DEBOUNCE_MS);
    };

    const onValueChanged = (model: SurveyModel) => {
      report(model);
      schedule(model);
    };
    const onCurrentPageChanged = (model: SurveyModel) => {
      report(model);
      if (timer.current) clearTimeout(timer.current);
      void flush(model);
    };
    const onVisibleChanged = (model: SurveyModel) => report(model);
    const onComplete_ = (model: SurveyModel) => {
      if (timer.current) clearTimeout(timer.current);
      onStatusChange?.("saving");
      void onComplete({
        data: model.data as Record<string, unknown>,
        currentPageNo: model.currentPageNo,
      })
        .then(() => onStatusChange?.("saved"))
        .catch((cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setError(message);
          onStatusChange?.("error", message);
        });
    };

    survey.onValueChanged.add(onValueChanged);
    survey.onCurrentPageChanged.add(onCurrentPageChanged);
    survey.onPageVisibleChanged.add(onVisibleChanged);
    survey.onComplete.add(onComplete_);

    return () => {
      survey.onValueChanged.remove(onValueChanged);
      survey.onCurrentPageChanged.remove(onCurrentPageChanged);
      survey.onPageVisibleChanged.remove(onVisibleChanged);
      survey.onComplete.remove(onComplete_);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [survey, readOnly, onSave, onComplete, onStatusChange, report]);

  useEffect(() => {
    if (readOnly) return undefined;

    // A `file` question posts to the uploads API and keeps the returned source id, so the file
    // is evidence in its own right rather than a blob inside an answer.
    const handler = async (
      _model: SurveyModel,
      options: {
        question: Question | null;
        files: File[];
        callback: (status: string, data?: unknown) => void;
      },
    ) => {
      try {
        const question = options.question as (Question & { uploadCategory?: string }) | null;
        const category = question?.uploadCategory ?? "other";
        const results = [] as Array<{ file: File; content: string }>;
        for (const file of options.files) {
          const form = new FormData();
          form.append("file", file);
          form.append("upload_category_code", category);
          if (sessionId) form.append("session_id", sessionId);
          if (projectId) form.append("onboarding_project_id", projectId);
          if (question?.semanticId) form.append("question_semantic_id", String(question.semanticId));
          const created = await uploadsApi.create(organizationId, form);
          results.push({ file, content: created.id });

          // The file may answer questions the respondent has not reached yet.
          const flag = uploadFlags([
            {
              upload_category_code: created.upload_category_code,
              processing_status: created.processing_status,
              original_filename: created.original_filename,
            },
          ]);
          for (const [name, value] of Object.entries(flag)) {
            if (value) survey.setVariable(name, true);
          }
        }
        options.callback("success", results);
        report(survey);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
        options.callback("error");
      }
    };

    survey.onUploadFiles.add(handler);
    return () => survey.onUploadFiles.remove(handler);
  }, [survey, organizationId, sessionId, projectId, readOnly, report]);

  return (
    <div className="survey-host">
      {error ? (
        <div className="banner banner--error" style={{ margin: 12 }}>
          {error}
        </div>
      ) : null}
      <Survey model={survey} />
    </div>
  );
}
